/**
 * Branding centralizzato di Bacco.
 *
 * Questa è la **sorgente unica** per nome prodotto, colori, logo e contatti.
 * Nessun componente deve ripetere letteralmente il nome del prodotto, i colori
 * di marca o gli indirizzi email: importare sempre da qui.
 *
 * I colori sono espressi in HSL "H S% L%" senza funzione `hsl()` perché vengono
 * iniettati come CSS custom properties e composti da Tailwind con l'opacità
 * (`hsl(var(--primary) / <alpha-value>)`).
 */

export interface BrandPalette {
  /** Colore primario di marca — bordeaux vino, richiamo diretto a Bacco. */
  readonly primary: string;
  readonly primaryForeground: string;
  /** Colore secondario — verde oliva, usato per accenti e stati positivi. */
  readonly secondary: string;
  readonly secondaryForeground: string;
  /** Accento caldo — ambra, usato per evidenziazioni e avvisi lievi. */
  readonly accent: string;
  readonly accentForeground: string;
}

export interface BrandCompany {
  readonly legalName: string;
  readonly websiteUrl: string;
  readonly supportEmail: string;
  readonly salesEmail: string;
  readonly noReplyEmail: string;
  readonly privacyEmail: string;
  readonly supportPhone: string;
}

export interface Branding {
  readonly productName: string;
  readonly productTagline: string;
  readonly productDescription: string;
  readonly logoPath: string;
  readonly logoMarkPath: string;
  readonly faviconPath: string;
  readonly iconPath: string;
  readonly maskableIconPath: string;
  readonly defaultLocale: string;
  readonly supportedLocales: readonly string[];
  readonly defaultCurrency: string;
  readonly defaultTimeZone: string;
  readonly palette: BrandPalette;
  readonly darkPalette: BrandPalette;
  readonly company: BrandCompany;
}

export const branding: Branding = {
  productName: 'Bacco',
  productTagline: 'Il gestionale che manda avanti il locale',
  productDescription:
    'Bacco: gestionale per ristoranti, pizzerie, bar e bistrot. Sala, cucina, cassa e magazzino in un unico sistema.',

  logoPath: '/brand/bacco-logo.svg',
  logoMarkPath: '/brand/bacco-mark.svg',
  faviconPath: '/favicon.svg',
  iconPath: '/icons/icon-512.png',
  maskableIconPath: '/icons/icon-maskable-512.png',

  defaultLocale: 'it-IT',
  supportedLocales: ['it-IT'],
  defaultCurrency: 'EUR',
  defaultTimeZone: 'Europe/Rome',

  palette: {
    primary: '340 46% 33%',
    primaryForeground: '40 33% 97%',
    secondary: '88 22% 34%',
    secondaryForeground: '40 33% 97%',
    accent: '38 84% 52%',
    accentForeground: '30 40% 12%',
  },
  darkPalette: {
    primary: '340 52% 58%',
    primaryForeground: '340 40% 12%',
    secondary: '88 28% 55%',
    secondaryForeground: '88 30% 10%',
    accent: '38 88% 60%',
    accentForeground: '30 45% 10%',
  },

  company: {
    legalName: 'Bacco Software',
    websiteUrl: 'https://bacco.app',
    supportEmail: 'supporto@bacco.app',
    salesEmail: 'commerciale@bacco.app',
    noReplyEmail: 'no-reply@bacco.app',
    privacyEmail: 'privacy@bacco.app',
    supportPhone: '+39 02 0000 0000',
  },
} as const;

/** Titolo di una pagina, sempre suffissato con il nome prodotto. */
export function pageTitle(section?: string): string {
  return section ? `${section} · ${branding.productName}` : `${branding.productName} — ${branding.productTagline}`;
}
